import { useState, useEffect, useRef } from "react";
import { GenerateVizaButton } from "./GenerateVizaButton";
import { useForm } from "react-hook-form";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import {
  Loader2,
  FileText,
  Check,
  Heart,
  CheckCircle,
  XCircle,
  AlertTriangle,
  AlertCircle,
  Activity,
  X,
} from "lucide-react";
import { patientApi } from "../services/api";
import { toast } from "sonner";
import { formatDate } from "../lib/utils";

// Calculare vârstă din data nașterii
const calculateAge = (birthDate) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// Multiplicator în funcție de vârstă pentru Testul Ruffier
// <6 ani: test opțional
// 6-9 ani (inclusiv 9): *0.75
// 10-13 ani (inclusiv 13): *0.85
// >=14 ani: *1
const getAgeMultiplier = (age) => {
  if (age === null || age === undefined) return { multiplicator: 1, optional: false };
  if (age < 6) return { multiplicator: 1, optional: true };
  if (age <= 9) return { multiplicator: 0.75, optional: false };
  if (age <= 13) return { multiplicator: 0.85, optional: false };
  return { multiplicator: 1, optional: false };
};

// Interpretare rezultat Ruffier
const interpretRuffier = (rezultat) => {
  if (rezultat < 0) {
    return { text: "Excelent", status: "excelent", color: "emerald" };
  } else if (rezultat < 3) {
    return { text: "Foarte bine", status: "foarte_bine", color: "emerald" };
  } else if (rezultat < 6) {
    return { text: "Bine", status: "bine", color: "emerald" };
  } else if (rezultat < 10) {
    return { text: "Mediu/Satisfăcător", status: "mediu", color: "yellow" };
  } else if (rezultat < 15) {
    return { text: "Nesatisfăcător", status: "nesatisfacator", color: "orange" };
  }
  return {
    text: "Adaptare foarte slabă la efort, MONITORIZARE",
    status: "slab",
    color: "red",
  };
};

// Interpretare VO2 relativ (pe valoarea corectată), pe sex.
//  • Masculin: <50 „Sub nivelul sportivilor de performanță" · 50-59 „Bun" ·
//    60-69 „Foarte bun" · 70-79 „Excelent" · ≥80 „Excepțional"
//  • Feminin:  <45 „Sub nivel competițional" · 45-54 „Bun" · 55-64 „Foarte bun" ·
//    65-74 „Excelent" · ≥75 „Excepțional"
const interpretVO2Relativ = (vo2, sex) => {
  const s = String(sex || "").toLowerCase();
  if (s.startsWith("f")) {
    if (vo2 < 45) return { text: "Sub nivel competițional", status: "sub_nivel" };
    if (vo2 < 55) return { text: "Bun", status: "bun" };
    if (vo2 < 65) return { text: "Foarte bun", status: "foarte_bun" };
    if (vo2 < 75) return { text: "Excelent", status: "excelent" };
    return { text: "Excepțional", status: "exceptional" };
  }
  // Masculin (default)
  if (vo2 < 50) return { text: "Sub nivelul sportivilor de performanță", status: "sub_nivel" };
  if (vo2 < 60) return { text: "Bun", status: "bun" };
  if (vo2 < 70) return { text: "Foarte bun", status: "foarte_bun" };
  if (vo2 < 80) return { text: "Excelent", status: "excelent" };
  return { text: "Excepțional", status: "exceptional" };
};

// Factor de corecție în funcție de vârstă
const getVO2CorrectionFactor = (age) => {
  if (age === null || age === undefined) return 1;
  if (age < 20) return 1.1;
  if (age > 35) return 0.87;
  return 1;
};

// Praguri STT pe intervale de greutate (kg)
// [excelent_max, foartebun_max, bun_max, mediu_max] - rezultat sub excelent_max = Excelent, peste mediu_max = Slab
const STT_THRESHOLDS = [
  { min: 50, max: 55, t: [5845, 6720, 7945, 9760] },
  { min: 55, max: 60, t: [6150, 7070, 8360, 10270] },
  { min: 60, max: 65, t: [6460, 7430, 8780, 10780] },
  { min: 65, max: 70, t: [6770, 7780, 9200, 11300] },
  { min: 70, max: 75, t: [7080, 8140, 9620, 11820] },
  { min: 75, max: 80, t: [7390, 8500, 10050, 12310] },
  { min: 80, max: 85, t: [7700, 8850, 10470, 12850] },
  { min: 85, max: 90, t: [8010, 9210, 10890, 13370] },
  { min: 90, max: 95, t: [8320, 9560, 11310, 13890] },
  { min: 95, max: 100, t: [8630, 9920, 11730, 14410] },
];

const getSTTBracket = (greutate) => {
  if (!greutate || greutate <= 0) return null;
  // [min, max): 55 → 55-60; pentru ≥100 → folosim ultimul interval
  if (greutate >= 100) return STT_THRESHOLDS[STT_THRESHOLDS.length - 1];
  if (greutate < 50) return null;
  return STT_THRESHOLDS.find((b) => greutate >= b.min && greutate < b.max);
};

const interpretSTT = (rezultat, greutate) => {
  const bracket = getSTTBracket(greutate);
  if (!bracket) return null;
  const [excelent, foarteBun, bun, mediu] = bracket.t;
  if (rezultat < excelent) return { text: "Excelent", status: "excelent" };
  if (rezultat < foarteBun) return { text: "Foarte bun", status: "foarte_bun" };
  if (rezultat < bun) return { text: "Bun", status: "bun" };
  if (rezultat < mediu) return { text: "Mediu", status: "mediu" };
  return { text: "Slab", status: "slab" };
};

// Extrage valoarea sistolică din string TA (ex. "120/80" → 120)
const parseTASystolic = (ta) => {
  if (!ta || typeof ta !== "string") return null;
  const match = ta.match(/^\s*(\d+(?:[.,]\d+)?)/);
  if (!match) return null;
  return parseFloat(match[1].replace(",", "."));
};

// Auto-formatare TA: utilizatorul tastează doar cifre, inserăm "/" automat după 3 cifre
// Acceptă maxim 3 cifre sistolice + "/" + 2 cifre diastolice → "xxx/xx"
const formatTA = (value) => {
  if (!value) return "";
  // Extrage doar cifrele
  const digits = String(value).replace(/\D/g, "").slice(0, 5);
  if (digits.length <= 3) return digits;
  return `${digits.slice(0, 3)}/${digits.slice(3)}`;
};

const getDefaultValues = (atelierData) => ({
  completat: atelierData?.completat || false,
  data_completare: atelierData?.data_completare || "",
  av_ortostatism: atelierData?.av_ortostatism || "",
  ta_ortostatism: atelierData?.ta_ortostatism || "",
  av_clinostatism: atelierData?.av_clinostatism || "",
  ta_clinostatism: atelierData?.ta_clinostatism || "",
  ruffier_activat: atelierData?.ruffier_activat || false,
  ruffier_p1: atelierData?.ruffier_p1 || "",
  ruffier_p2: atelierData?.ruffier_p2 || "",
  ruffier_p3: atelierData?.ruffier_p3 || "",
  ruffier_rezultat: atelierData?.ruffier_rezultat || "",
  ruffier_rezultat_brut: atelierData?.ruffier_rezultat_brut || "",
  ruffier_multiplicator: atelierData?.ruffier_multiplicator || "",
  ruffier_interpretare: atelierData?.ruffier_interpretare || "",
  ruffier_revenire: atelierData?.ruffier_revenire || "",
  ruffier_reevaluare: atelierData?.ruffier_reevaluare || false,
  ruffier_reevaluare_zile: atelierData?.ruffier_reevaluare_zile || "",
  ruffier_status: atelierData?.ruffier_status || "",
  astrand_activat: atelierData?.astrand_activat || false,
  astrand_p2: atelierData?.astrand_p2 || "",
  astrand_p4: atelierData?.astrand_p4 || "",
  astrand_p6: atelierData?.astrand_p6 || "",
  astrand_p9: atelierData?.astrand_p9 || "",
  astrand_p12: atelierData?.astrand_p12 || "",
  astrand_ta6: atelierData?.astrand_ta6 || "",
  astrand_ta9: atelierData?.astrand_ta9 || "",
  astrand_ta12: atelierData?.astrand_ta12 || "",
  astrand_vo2_max: atelierData?.astrand_vo2_max || "",
  astrand_vo2_relativ: atelierData?.astrand_vo2_relativ || "",
  astrand_factor_corectie: atelierData?.astrand_factor_corectie || "",
  astrand_vo2_relativ_corectat: atelierData?.astrand_vo2_relativ_corectat || "",
  astrand_interpretare: atelierData?.astrand_interpretare || "",
  astrand_interpretare_status: atelierData?.astrand_interpretare_status || "",
  astrand_conformitate: atelierData?.astrand_conformitate || "",
  astrand_conform: atelierData?.astrand_conform || false,
  astrand_revenire: atelierData?.astrand_revenire || "",
  astrand_revenire_status: atelierData?.astrand_revenire_status || "",
  astrand_revenire_recomandare: atelierData?.astrand_revenire_recomandare || "",
  astrand_recomandare_dismissed: atelierData?.astrand_recomandare_dismissed || false,
  ruffier_recomandare_dismissed: atelierData?.ruffier_recomandare_dismissed || false,
  pipa: atelierData?.pipa || false,
  monitorizare_puls: atelierData?.monitorizare_puls || false,
  stt_activat: atelierData?.stt_activat || false,
  stt_rezultat: atelierData?.stt_rezultat || "",
  stt_calificativ: atelierData?.stt_calificativ || "",
  stt_calificativ_status: atelierData?.stt_calificativ_status || "",
  observatii: atelierData?.observatii || "",
});

// Icon în funcție de statusul rezultatului
const StatusIcon = ({ status, className = "w-5 h-5" }) => {
  if (status === "excelent" || status === "foarte_bine" || status === "bine") {
    return <CheckCircle className={`${className} text-emerald-500`} />;
  }
  if (status === "mediu") {
    return <CheckCircle className={`${className} text-yellow-500`} />;
  }
  if (status === "nesatisfacator") {
    return <AlertCircle className={`${className} text-orange-500`} />;
  }
  if (status === "slab") {
    return <XCircle className={`${className} text-red-500`} />;
  }
  return null;
};

export const Atelier2Button = ({ patientId, patientData, atelierData, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dataNasterii = patientData?.date_personale?.data_nasterii || "";
  const sex = patientData?.date_personale?.sex || "";
  const greutatePacient = parseFloat(patientData?.atelier_1?.greutate) || 0;
  const age = calculateAge(dataNasterii);
  const { multiplicator, optional: isOptional } = getAgeMultiplier(age);

  const { register, handleSubmit, setValue, watch, reset, getValues } = useForm({
    defaultValues: getDefaultValues(atelierData),
  });

  const completat = watch("completat");
  const watchedValues = watch();

  // Auto-format TA fields (xxx/yy sau xxx/yyy) — la 3 cifre se pune automat "/".
  // Diastolica acceptă 2 SAU 3 cifre (ambele forme sunt valide medical).
  const taPrevRef = useRef({ ta6: "", ta9: "", ta12: "" });
  const formatTAValue = (raw, prev) => {
    let cleaned = (raw || "").replace(/[^\d/]/g, "");
    const parts = cleaned.split("/");
    if (parts.length > 2) cleaned = parts[0] + "/" + parts.slice(1).join("");
    const growing = cleaned.length > (prev || "").length;
    if (growing && !cleaned.includes("/") && cleaned.length >= 3) {
      cleaned = cleaned.slice(0, 3) + "/" + cleaned.slice(3);
    }
    const [sys, dia = ""] = cleaned.split("/");
    return sys.slice(0, 3) + (cleaned.includes("/") ? "/" + dia.slice(0, 3) : "");
  };
  const handleTAChange = (field) => (e) => {
    const prev = taPrevRef.current[field] || "";
    const formatted = formatTAValue(e.target.value, prev);
    if (formatted !== e.target.value) {
      setValue(`astrand_${field}`, formatted, { shouldDirty: true, shouldValidate: false });
    }
    taPrevRef.current[field] = formatted;
  };
  useEffect(() => {
    taPrevRef.current = {
      ta6: watchedValues.astrand_ta6 || "",
      ta9: watchedValues.astrand_ta9 || "",
      ta12: watchedValues.astrand_ta12 || "",
    };
    // Sincronizare doar la deschidere — nu la fiecare typing.
  }, [open, watchedValues.astrand_ta12, watchedValues.astrand_ta6, watchedValues.astrand_ta9]);

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      reset(getDefaultValues(atelierData));
    }
  };

  // Auto-completare P1 = parte întreagă din AV ortostatism / 4
  useEffect(() => {
    if (!watchedValues.ruffier_activat) return;
    const av = parseFloat(watchedValues.av_ortostatism);
    if (!isNaN(av) && av > 0) {
      const p1 = Math.floor(av / 4);
      if (String(p1) !== String(watchedValues.ruffier_p1)) {
        setValue("ruffier_p1", p1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValues.av_ortostatism, watchedValues.ruffier_activat]);

  // Calcul automat rezultat Ruffier când P1, P2, P3 sunt completate
  useEffect(() => {
    if (!watchedValues.ruffier_activat) return;
    const p1 = parseFloat(watchedValues.ruffier_p1);
    const p2 = parseFloat(watchedValues.ruffier_p2);
    const p3 = parseFloat(watchedValues.ruffier_p3);
    if (isNaN(p1) || isNaN(p2) || isNaN(p3) || p1 <= 0 || p2 <= 0 || p3 <= 0) {
      return;
    }
    const rezultatBrut = ((p1 + p2 + p3) * 4 - 200) / 10;
    const rezultatFinal = rezultatBrut * multiplicator;
    const interpretare = interpretRuffier(rezultatFinal);

    setValue("ruffier_rezultat_brut", rezultatBrut.toFixed(2));
    setValue("ruffier_rezultat", rezultatFinal.toFixed(2));
    setValue("ruffier_multiplicator", multiplicator);
    setValue("ruffier_interpretare", interpretare.text);
    // Când statusul se schimbă, resetăm dismiss-ul recomandării.
    if (watchedValues.ruffier_status !== interpretare.status) {
      setValue("ruffier_recomandare_dismissed", false);
    }
    setValue("ruffier_status", interpretare.status);
    // Auto-marcare „Revenire slabă" dacă P3 depășește P1 cu ≥ 8 bpm.
    // Nu suprascrie o alegere manuală „buna" dacă condiția nu se mai aplică.
    if (p3 - p1 >= 8) {
      if (watchedValues.ruffier_revenire !== "slaba") setValue("ruffier_revenire", "slaba");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedValues.ruffier_p1,
    watchedValues.ruffier_p2,
    watchedValues.ruffier_p3,
    watchedValues.ruffier_activat,
    multiplicator,
  ]);

  // Calcul Astrand VO2 când se completează P6' și avem Greutate + Sex
  useEffect(() => {
    if (!watchedValues.astrand_activat) return;
    const p6 = parseFloat(watchedValues.astrand_p6);
    if (!greutatePacient || isNaN(p6) || p6 <= 0 || !sex) return;

    // Coeficient în funcție de sex
    const coef = sex === "Masculin" ? 0.00212 : 0.00193;
    const relMultiplier = sex === "Masculin" ? 1.25 : 1.45;

    // VO2 max = ((coef * (Greutate * 2.1) * 6.12) + 0.3) / (1.03 * P6' - 67)
    //   • coef = 0.00212 (masculin) / 0.00193 (feminin)
    const numerator = coef * (greutatePacient * 2.1) * 6.12 + 0.3;
    const denominator = 1.03 * p6 - 67;
    if (!denominator) return;
    const vo2Max = numerator / denominator;

    // VO2 relativ = (VO2 max / Greutate) * mult
    const vo2Relativ = (vo2Max / greutatePacient) * relMultiplier;

    // Factor de corecție vârstă
    const factorCorectie = getVO2CorrectionFactor(age);
    const vo2RelativCorectat = vo2Relativ * factorCorectie;

    // Interpretare (folosind valoarea corectată)
    const interpretare = interpretVO2Relativ(vo2RelativCorectat);

    // Conformitate etapă pregătire
    const prag = sex === "Masculin" ? 52 : 49;
    const conform = vo2RelativCorectat >= prag;
    const conformitate = conform
      ? "Conform cerințelor etapei de pregătire"
      : "Sub cerințele etapei de pregătire";

    setValue("astrand_vo2_max", vo2Max.toFixed(2));
    setValue("astrand_vo2_relativ", vo2Relativ.toFixed(2));
    setValue("astrand_factor_corectie", factorCorectie);
    setValue("astrand_vo2_relativ_corectat", vo2RelativCorectat.toFixed(2));
    setValue("astrand_interpretare", interpretare.text);
    setValue("astrand_interpretare_status", interpretare.status);
    setValue("astrand_conformitate", conformitate);
    setValue("astrand_conform", conform);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedValues.astrand_p6,
    watchedValues.astrand_activat,
    greutatePacient,
    sex,
    age,
  ]);

  // Evaluare revenire: P 9' vs AV ortostatism; TA 9' vs TA ortostatism
  useEffect(() => {
    if (!watchedValues.astrand_activat) {
      return;
    }
    const av = parseFloat(watchedValues.av_ortostatism);
    const p9 = parseFloat(watchedValues.astrand_p9);
    const taOrto = parseTASystolic(watchedValues.ta_ortostatism);
    const ta9 = parseTASystolic(watchedValues.astrand_ta9);

    // Avem nevoie cel puțin de AV ortostatism și P9' pentru orice evaluare
    if (isNaN(av) || av <= 0 || isNaN(p9) || p9 <= 0) {
      if (watchedValues.astrand_revenire) {
        setValue("astrand_revenire", "");
        setValue("astrand_revenire_status", "");
        setValue("astrand_revenire_recomandare", "");
      }
      return;
    }

    // Condiția "Revenire grea": P 9' > AV ortostatism + 30%
    if (p9 > av * 1.3) {
      setValue("astrand_revenire", "Revenire grea");
      setValue("astrand_revenire_status", "grea");
      setValue("astrand_revenire_recomandare", "Atenție la suprasolicitare");
      setValue("astrand_recomandare_dismissed", false);
      return;
    }

    // Condiția "Revenire bună": P9' în ±10% AV orto ȘI TA9' în ±10% TA orto
    const p9InRange = p9 >= av * 0.9 && p9 <= av * 1.1;
    // TA9 și TA orto trebuie să existe pentru evaluarea "buna"
    if (taOrto !== null && ta9 !== null) {
      const ta9InRange = ta9 >= taOrto * 0.9 && ta9 <= taOrto * 1.1;
      if (p9InRange && ta9InRange) {
        setValue("astrand_revenire", "Revenire bună");
        setValue("astrand_revenire_status", "buna");
        setValue("astrand_revenire_recomandare", "");
        return;
      }
    }

    // Niciuna dintre condiții: curăță
    setValue("astrand_revenire", "");
    setValue("astrand_revenire_status", "");
    setValue("astrand_revenire_recomandare", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedValues.astrand_p9,
    watchedValues.astrand_ta9,
    watchedValues.av_ortostatism,
    watchedValues.ta_ortostatism,
    watchedValues.astrand_activat,
  ]);

  // Calcul STT (Sistolic Tension Time / Watt / Kg corp)
  useEffect(() => {
    if (!watchedValues.stt_activat) return;
    const fc6 = parseFloat(watchedValues.astrand_p6);
    const ta6Sistolica = parseTASystolic(watchedValues.astrand_ta6);
    if (isNaN(fc6) || fc6 <= 0 || ta6Sistolica === null || ta6Sistolica <= 0) {
      if (watchedValues.stt_rezultat) {
        setValue("stt_rezultat", "");
        setValue("stt_calificativ", "");
        setValue("stt_calificativ_status", "");
      }
      return;
    }
    // STT = (FC 6' * TA sistolică 6') / 2.1
    const stt = (fc6 * ta6Sistolica) / 2.1;
    setValue("stt_rezultat", stt.toFixed(2));

    const cal = interpretSTT(stt, greutatePacient);
    if (cal) {
      setValue("stt_calificativ", cal.text);
      setValue("stt_calificativ_status", cal.status);
    } else {
      setValue("stt_calificativ", "");
      setValue("stt_calificativ_status", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedValues.stt_activat,
    watchedValues.astrand_p6,
    watchedValues.astrand_ta6,
    greutatePacient,
  ]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (data.completat && !data.data_completare) {
        data.data_completare = new Date().toISOString();
      }

      const numericFields = [
        "av_ortostatism",
        "av_clinostatism",
        "ruffier_p1",
        "ruffier_p2",
        "ruffier_p3",
        "ruffier_rezultat",
        "ruffier_rezultat_brut",
        "ruffier_multiplicator",
        "astrand_p2",
        "astrand_p4",
        "astrand_p6",
        "astrand_p9",
        "astrand_p12",
        "astrand_vo2_max",
        "astrand_vo2_relativ",
        "astrand_factor_corectie",
        "astrand_vo2_relativ_corectat",
        "stt_rezultat",
      ];
      numericFields.forEach((field) => {
        const val = data[field];
        if (val === "" || val === undefined || val === null) {
          data[field] = 0;
        } else {
          const parsed = parseFloat(val);
          data[field] = Number.isFinite(parsed) ? parsed : 0;
        }
      });

      const updated = await patientApi.updateAtelier2(patientId, data);
      toast.success("Atelier 2 salvat cu succes!");
      setOpen(false);
      if (onUpdate) onUpdate(updated);
    } catch (error) {
      console.error("Failed to update atelier:", error);
      const detail = error?.response?.data?.detail;
      if (detail) {
        const msg = typeof detail === 'string' ? detail : JSON.stringify(detail).slice(0, 200);
        toast.error(`Eroare salvare: ${msg}`);
      } else {
        toast.error("Eroare la salvarea atelierului. Verifică datele introduse.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isCompleted = atelierData?.completat;
  const rezultat = parseFloat(watchedValues.ruffier_rezultat);
  const hasValidResult = !isNaN(rezultat) && watchedValues.ruffier_interpretare;

  // Background color pentru rezultatul interpretării
  const resultBgClass = {
    excelent: "bg-emerald-100 border-emerald-300 text-emerald-800",
    foarte_bine: "bg-emerald-100 border-emerald-300 text-emerald-800",
    bine: "bg-emerald-100 border-emerald-300 text-emerald-800",
    mediu: "bg-yellow-100 border-yellow-300 text-yellow-800",
    nesatisfacator: "bg-orange-100 border-orange-300 text-orange-800",
    slab: "bg-red-100 border-red-300 text-red-800",
  }[watchedValues.ruffier_status] || "bg-muted";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant={isCompleted ? "default" : "outline"}
          className="gap-2 flex-1"
          data-testid="atelier-2-btn"
        >
          {isCompleted ? <Check className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
          Atelier 2
          {isCompleted && (
            <Badge variant="secondary" className="ml-1 text-xs">
              Completat
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle
            className="text-xl"
            style={{ fontFamily: "'Work Sans', sans-serif" }}
          >
            Atelier 2 - Evaluare Cardiovasculară
          </DialogTitle>
          {atelierData?.data_completare && (
            <p className="text-sm text-muted-foreground">
              Completat la: {formatDate(atelierData.data_completare)}
            </p>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mt-4">
          {/* Măsurători Cardiace */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Heart className="w-4 h-4 text-primary" />
                Măsurători Cardiace
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="av_ortostatism">AV ortostatism-sezut (bpm)</Label>
                  <Input
                    id="av_ortostatism"
                    type="number"
                    step="1"
                    placeholder="ex: 80"
                    data-testid="atelier-2-av-orto"
                    {...register("av_ortostatism")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ta_ortostatism">TA ortostatism- sezut (mmHg)</Label>
                  <Input
                    id="ta_ortostatism"
                    type="text"
                    placeholder="xxx/xx"
                    inputMode="numeric"
                    maxLength={6}
                    data-testid="atelier-2-ta-orto"
                    value={watchedValues.ta_ortostatism || ""}
                    onChange={(e) => setValue("ta_ortostatism", formatTA(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="av_clinostatism">AV clinostatism (bpm)</Label>
                  <Input
                    id="av_clinostatism"
                    type="number"
                    step="1"
                    placeholder="ex: 65"
                    data-testid="atelier-2-av-clino"
                    {...register("av_clinostatism")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ta_clinostatism">TA clinostatism (mmHg)</Label>
                  <Input
                    id="ta_clinostatism"
                    type="text"
                    placeholder="xxx/xx"
                    inputMode="numeric"
                    maxLength={6}
                    data-testid="atelier-2-ta-clino"
                    value={watchedValues.ta_clinostatism || ""}
                    onChange={(e) => setValue("ta_clinostatism", formatTA(e.target.value))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Separator />

          {/* Testul Ruffier */}
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary" />
                  Test de adaptare la efort Ruffier
                  {isOptional && (
                    <Badge variant="outline" className="text-xs">
                      Opțional (vârstă &lt; 6 ani)
                    </Badge>
                  )}
                </CardTitle>
                <Button
                  type="button"
                  variant={watchedValues.ruffier_activat ? "secondary" : "default"}
                  size="sm"
                  onClick={() => setValue("ruffier_activat", !watchedValues.ruffier_activat)}
                  data-testid="atelier-2-ruffier-toggle"
                >
                  {watchedValues.ruffier_activat ? "Închide test" : "Deschide test"}
                </Button>
              </div>
              {age !== null && (
                <p className="text-xs text-muted-foreground mt-1">
                  Vârstă: {age} ani · Multiplicator aplicat: ×{multiplicator}
                </p>
              )}
            </CardHeader>

            {watchedValues.ruffier_activat && (
              <CardContent className="space-y-4">
                {isOptional && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Pentru vârsta sub 6 ani, acest test este <strong>opțional</strong>. Se
                      aplică multiplicator ×1.
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ruffier_p1" className="flex items-center gap-1">
                      P1
                      <span className="text-xs text-muted-foreground">(auto: AV orto / 4)</span>
                    </Label>
                    <Input
                      id="ruffier_p1"
                      type="number"
                      step="1"
                      className="bg-white"
                      placeholder="auto-completat"
                      data-testid="atelier-2-p1"
                      {...register("ruffier_p1")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ruffier_p2">P2</Label>
                    <Input
                      id="ruffier_p2"
                      type="number"
                      step="1"
                      className="bg-white"
                      placeholder="ex: 30"
                      data-testid="atelier-2-p2"
                      {...register("ruffier_p2")}
                      onInput={(e) => {
                        // Auto-focus la P3 când s-au tastat 2 cifre (valorile Ruffier
                        // sunt puls pe 15 secunde, deci întotdeauna 2 cifre).
                        const val = String(e.target.value || "");
                        if (val.length === 2) {
                          const next = document.getElementById("ruffier_p3");
                          if (next) {
                            next.focus();
                            if (typeof next.select === "function") next.select();
                          }
                        }
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ruffier_p3">P3</Label>
                    <Input
                      id="ruffier_p3"
                      type="number"
                      step="1"
                      className="bg-white"
                      placeholder="ex: 25"
                      data-testid="atelier-2-p3"
                      {...register("ruffier_p3")}
                    />
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  Formulă: ((P1 + P2 + P3) × 4 − 200) / 10 × {multiplicator}
                </p>

                {/* Rezultat */}
                {hasValidResult && (
                  <div className={`p-4 rounded-lg border ${resultBgClass}`} data-testid="atelier-2-rezultat">
                    <div className="flex items-start gap-3">
                      <StatusIcon status={watchedValues.ruffier_status} className="w-6 h-6 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-sm font-medium opacity-80">Rezultat:</span>
                          <span className="text-2xl font-bold">
                            {parseFloat(watchedValues.ruffier_rezultat).toFixed(2)}
                          </span>
                          {watchedValues.ruffier_rezultat_brut &&
                            parseFloat(watchedValues.ruffier_multiplicator) !== 1 && (
                              <span className="text-xs opacity-70">
                                (brut: {parseFloat(watchedValues.ruffier_rezultat_brut).toFixed(2)} × {multiplicator})
                              </span>
                            )}
                        </div>
                        <p className="font-semibold mt-1">{watchedValues.ruffier_interpretare}</p>
                      </div>
                    </div>
                    {(watchedValues.ruffier_status === "nesatisfacator" || watchedValues.ruffier_status === "slab") && !watchedValues.ruffier_recomandare_dismissed && (
                      <div className="mt-3 pt-3 border-t border-current/20 flex items-start gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-semibold mb-1">Recomandare:</p>
                          <p className="text-sm">
                            {watchedValues.ruffier_status === "nesatisfacator"
                              ? "Monitorizare puls"
                              : "Monitorizarea pulsului sportivului în timpul antrenamentului a toleranței la efort și program individual de antrenament"}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setValue("ruffier_recomandare_dismissed", true)}
                          className="shrink-0 p-1 rounded hover:bg-red-100 text-red-600 transition"
                          title="Șterge recomandarea"
                          data-testid="atelier-2-ruffier-reco-clear"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Revenire după Ruffier — auto-marcată „slabă" dacă P3 - P1 ≥ 8 */}
                {hasValidResult && (
                  <div className="p-3 rounded-lg border bg-slate-50/40 space-y-2" data-testid="atelier-2-ruffier-revenire">
                    <Label className="text-sm font-semibold">Revenire</Label>
                    <p className="text-xs text-muted-foreground">
                      Auto-marcare &bdquo;Revenire slabă&rdquo; dacă P3 depășește P1 cu ≥ 8 bpm.
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="ruffier_revenire_buna"
                          checked={watchedValues.ruffier_revenire === "buna"}
                          onCheckedChange={(v) => setValue("ruffier_revenire", v ? "buna" : "")}
                          data-testid="ruffier-revenire-buna"
                        />
                        <Label htmlFor="ruffier_revenire_buna" className="text-sm cursor-pointer">
                          Revenire bună
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="ruffier_revenire_slaba"
                          checked={watchedValues.ruffier_revenire === "slaba"}
                          onCheckedChange={(v) => setValue("ruffier_revenire", v ? "slaba" : "")}
                          data-testid="ruffier-revenire-slaba"
                        />
                        <Label htmlFor="ruffier_revenire_slaba" className="text-sm cursor-pointer">
                          Revenire slabă
                        </Label>
                      </div>
                    </div>
                    {/* Reevaluare probă de efort */}
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-200">
                      <Checkbox
                        id="ruffier_reevaluare"
                        checked={!!watchedValues.ruffier_reevaluare}
                        onCheckedChange={(v) => setValue("ruffier_reevaluare", !!v)}
                        data-testid="ruffier-reevaluare"
                      />
                      <Label htmlFor="ruffier_reevaluare" className="text-sm cursor-pointer">
                        Reevaluare probă de efort în
                      </Label>
                      <Input
                        type="number"
                        min="1"
                        value={watchedValues.ruffier_reevaluare_zile}
                        onChange={(e) => setValue("ruffier_reevaluare_zile", e.target.value)}
                        disabled={!watchedValues.ruffier_reevaluare}
                        className="w-20 h-8 text-sm"
                        placeholder="—"
                        data-testid="ruffier-reevaluare-zile"
                      />
                      <span className="text-sm">zile</span>
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Testul Astrand */}
          <Card className="border-emerald-500/20 bg-emerald-50/30">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-600" />
                  Test capacitate de efort Astrand
                </CardTitle>
                <Button
                  type="button"
                  variant={watchedValues.astrand_activat ? "secondary" : "default"}
                  size="sm"
                  onClick={() => setValue("astrand_activat", !watchedValues.astrand_activat)}
                  data-testid="atelier-2-astrand-toggle"
                >
                  {watchedValues.astrand_activat ? "Închide test" : "Deschide test"}
                </Button>
              </div>
              {sex && greutatePacient > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  Sex: {sex} · Greutate (din Atelier 1): {greutatePacient} kg
                </p>
              )}
              {(!sex || !greutatePacient) && watchedValues.astrand_activat && (
                <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {!sex && "Sexul trebuie completat în fișa pacientului. "}
                  {!greutatePacient && "Greutatea trebuie completată în Atelier 1."}
                </p>
              )}
            </CardHeader>

            {watchedValues.astrand_activat && (
              <CardContent className="space-y-4">
                {/* Puls (P) */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Puls (bpm)</Label>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="astrand_p2" className="text-xs">P 2&apos;</Label>
                      <Input
                        id="astrand_p2"
                        type="number"
                        step="1"
                        className="bg-white"
                        data-testid="atelier-2-astrand-p2"
                        {...register("astrand_p2")}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="astrand_p4" className="text-xs">P 4&apos;</Label>
                      <Input
                        id="astrand_p4"
                        type="number"
                        step="1"
                        className="bg-white"
                        data-testid="atelier-2-astrand-p4"
                        {...register("astrand_p4")}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="astrand_p6" className="text-xs font-semibold">
                        P 6&apos; <span className="text-emerald-600">*</span>
                      </Label>
                      <Input
                        id="astrand_p6"
                        type="number"
                        step="1"
                        className="bg-white font-semibold"
                        data-testid="atelier-2-astrand-p6"
                        {...register("astrand_p6")}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="astrand_p9" className="text-xs">P 9&apos;</Label>
                      <Input
                        id="astrand_p9"
                        type="number"
                        step="1"
                        className="bg-white"
                        data-testid="atelier-2-astrand-p9"
                        {...register("astrand_p9")}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="astrand_p12" className="text-xs">P 12&apos;</Label>
                      <Input
                        id="astrand_p12"
                        type="number"
                        step="1"
                        className="bg-white"
                        data-testid="atelier-2-astrand-p12"
                        {...register("astrand_p12")}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    <span className="text-emerald-600">*</span> P 6&apos; este folosit în calculul VO2
                  </p>
                </div>

                {/* TA */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Tensiune arterială (mmHg)</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="astrand_ta6" className="text-xs">TA 6&apos;</Label>
                      <Input
                        id="astrand_ta6"
                        type="text"
                        inputMode="numeric"
                        maxLength={7}
                        placeholder="ex: 130/85 sau 130/105"
                        className="bg-white"
                        data-testid="atelier-2-astrand-ta6"
                        {...register("astrand_ta6", { onChange: handleTAChange("ta6") })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="astrand_ta9" className="text-xs">TA 9&apos;</Label>
                      <Input
                        id="astrand_ta9"
                        type="text"
                        inputMode="numeric"
                        maxLength={7}
                        placeholder="ex: 135/85 sau 135/105"
                        className="bg-white"
                        data-testid="atelier-2-astrand-ta9"
                        {...register("astrand_ta9", { onChange: handleTAChange("ta9") })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="astrand_ta12" className="text-xs">TA 12&apos;</Label>
                      <Input
                        id="astrand_ta12"
                        type="text"
                        inputMode="numeric"
                        maxLength={7}
                        placeholder="ex: 140/90 sau 140/110"
                        className="bg-white"
                        data-testid="atelier-2-astrand-ta12"
                        {...register("astrand_ta12", { onChange: handleTAChange("ta12") })}
                      />
                    </div>
                  </div>
                </div>

                {/* Rezultate VO2 */}
                {watchedValues.astrand_vo2_max !== "" && watchedValues.astrand_vo2_max !== undefined && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-lg bg-emerald-100 border border-emerald-300">
                      <div className="space-y-1">
                        <Label htmlFor="astrand_vo2_max" className="text-sm font-medium text-emerald-900">
                          VO₂ max
                        </Label>
                        <Input
                          id="astrand_vo2_max"
                          type="number"
                          step="0.01"
                          className="bg-white font-semibold"
                          data-testid="atelier-2-astrand-vo2-max"
                          {...register("astrand_vo2_max")}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="astrand_vo2_relativ" className="text-sm font-medium text-emerald-900">
                          VO₂ relativ
                        </Label>
                        <Input
                          id="astrand_vo2_relativ"
                          type="number"
                          step="0.01"
                          className="bg-white font-semibold"
                          data-testid="atelier-2-astrand-vo2-relativ"
                          {...register("astrand_vo2_relativ")}
                        />
                      </div>
                    </div>

                    {/* Factor de corecție */}
                    {parseFloat(watchedValues.astrand_factor_corectie) !== 1 && (
                      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm">
                        <p className="text-blue-900">
                          <strong>Factorul de corecție este</strong> ×{watchedValues.astrand_factor_corectie}
                          {age !== null && age < 20 && <span className="text-xs ml-2">(vârstă &lt; 20 ani)</span>}
                          {age !== null && age > 35 && <span className="text-xs ml-2">(vârstă &gt; 35 ani)</span>}
                        </p>
                        <p className="text-blue-900 mt-1">
                          VO₂ relativ corectat: <strong>{watchedValues.astrand_vo2_relativ_corectat}</strong>
                        </p>
                      </div>
                    )}

                    {/* Interpretare cu icon */}
                    {watchedValues.astrand_interpretare && (
                      <div
                        className={`p-4 rounded-lg border flex items-start gap-3 ${
                          ["bun", "foarte_bun", "excelent", "exceptional"].includes(watchedValues.astrand_interpretare_status)
                            ? "bg-emerald-100 border-emerald-300 text-emerald-800"
                            : ["sub_nivel"].includes(watchedValues.astrand_interpretare_status)
                            ? "bg-red-100 border-red-300 text-red-800"
                            : "bg-orange-100 border-orange-300 text-orange-800"
                        }`}
                        data-testid="atelier-2-astrand-interpretare"
                      >
                        {["bun", "foarte_bun", "excelent", "exceptional"].includes(watchedValues.astrand_interpretare_status) ? (
                          <CheckCircle className="w-6 h-6 mt-0.5" />
                        ) : ["sub_nivel"].includes(watchedValues.astrand_interpretare_status) ? (
                          <XCircle className="w-6 h-6 mt-0.5" />
                        ) : (
                          <AlertCircle className="w-6 h-6 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <p className="text-xs opacity-80">Evaluare VO₂ relativ</p>
                          <p className="text-lg font-bold">{watchedValues.astrand_interpretare}</p>
                        </div>
                      </div>
                    )}

                    {/* Conformitate etapă pregătire */}
                    {watchedValues.astrand_conformitate && sex && (
                      <div
                        className={`p-3 rounded-lg border ${
                          watchedValues.astrand_conform
                            ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                            : "bg-red-50 border-red-300"
                        }`}
                        data-testid="atelier-2-astrand-conformitate"
                      >
                        <p className={`text-sm font-medium ${watchedValues.astrand_conform ? "" : "text-red-700"}`}>
                          {watchedValues.astrand_conformitate}
                        </p>
                        <p className="text-xs opacity-70 mt-0.5">
                          Prag {sex === "Masculin" ? "masculin" : "feminin"}: ≥
                          {sex === "Masculin" ? 52 : 49}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Evaluare revenire (P9' și TA9' vs ortostatism) */}
                {watchedValues.astrand_revenire && (
                  <div
                    className={`p-3 rounded-lg border flex items-start gap-3 ${
                      watchedValues.astrand_revenire_status === "buna"
                        ? "bg-emerald-100 border-emerald-300 text-emerald-800"
                        : "bg-red-100 border-red-300 text-red-800"
                    }`}
                    data-testid="atelier-2-astrand-revenire"
                  >
                    {watchedValues.astrand_revenire_status === "buna" ? (
                      <CheckCircle className="w-5 h-5 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className="text-xs opacity-80">Evaluare revenire</p>
                      <p className="font-semibold">{watchedValues.astrand_revenire}</p>
                      {watchedValues.astrand_revenire_recomandare && !watchedValues.astrand_recomandare_dismissed && (
                        <p className="text-sm mt-1 flex items-start gap-2">
                          <span className="flex-1">
                            <strong>Recomandare:</strong> {watchedValues.astrand_revenire_recomandare}
                          </span>
                          <button
                            type="button"
                            onClick={() => setValue("astrand_recomandare_dismissed", true)}
                            className="shrink-0 p-1 rounded hover:bg-red-200 text-red-700 transition"
                            title="Șterge recomandarea"
                            data-testid="atelier-2-astrand-reco-clear"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Test STT */}
          <Card className="border-indigo-500/20 bg-indigo-50/30">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-indigo-600" />
                  STT (Sistolic Tension Time) / Watt / Kg corp
                </CardTitle>
                <Button
                  type="button"
                  variant={watchedValues.stt_activat ? "secondary" : "default"}
                  size="sm"
                  onClick={() => setValue("stt_activat", !watchedValues.stt_activat)}
                  data-testid="atelier-2-stt-toggle"
                >
                  {watchedValues.stt_activat ? "Închide test" : "Deschide test"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Folosește FC 6&apos; și TA sistolică 6&apos; din testul Astrand
                {greutatePacient > 0 && ` · Greutate: ${greutatePacient} kg`}
              </p>
            </CardHeader>

            {watchedValues.stt_activat && (
              <CardContent className="space-y-4">
                {(!watchedValues.astrand_p6 || !watchedValues.astrand_ta6) && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Completează <strong>P 6&apos;</strong> și <strong>TA 6&apos;</strong> în testul Astrand pentru a calcula STT.
                    </span>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  Formulă: STT = (FC 6&apos; × TA sistolică 6&apos;) / 2.1
                </p>

                {watchedValues.stt_rezultat && (
                  <>
                    <div className="p-4 rounded-lg bg-indigo-100 border border-indigo-300">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-medium text-indigo-900">Rezultat STT:</span>
                        <span className="text-2xl font-bold text-indigo-900">
                          {parseFloat(watchedValues.stt_rezultat).toFixed(2)}
                        </span>
                      </div>
                      <p className="text-sm text-indigo-800 italic mt-2">
                        Adaptarea cardiovasculară după efort submaximal, fiind un indice de eficiență miocardică.
                      </p>
                    </div>

                    {watchedValues.stt_calificativ ? (
                      <div
                        className={`p-4 rounded-lg border flex items-start gap-3 ${
                          watchedValues.stt_calificativ_status === "excelent" ||
                          watchedValues.stt_calificativ_status === "foarte_bun"
                            ? "bg-emerald-100 border-emerald-300 text-emerald-800"
                            : watchedValues.stt_calificativ_status === "bun"
                            ? "bg-yellow-100 border-yellow-300 text-yellow-800"
                            : watchedValues.stt_calificativ_status === "mediu"
                            ? "bg-orange-100 border-orange-300 text-orange-800"
                            : "bg-red-100 border-red-300 text-red-800"
                        }`}
                        data-testid="atelier-2-stt-calificativ"
                      >
                        {watchedValues.stt_calificativ_status === "excelent" ||
                        watchedValues.stt_calificativ_status === "foarte_bun" ? (
                          <CheckCircle className="w-6 h-6 mt-0.5 text-emerald-600" />
                        ) : watchedValues.stt_calificativ_status === "bun" ? (
                          <CheckCircle className="w-6 h-6 mt-0.5 text-yellow-600" />
                        ) : watchedValues.stt_calificativ_status === "mediu" ? (
                          <AlertCircle className="w-6 h-6 mt-0.5" />
                        ) : (
                          <XCircle className="w-6 h-6 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <p className="text-xs opacity-80">Calificativ</p>
                          <p className="text-lg font-bold">{watchedValues.stt_calificativ}</p>
                        </div>
                      </div>
                    ) : (
                      greutatePacient > 0 && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                          Greutatea ({greutatePacient} kg) este în afara intervalelor de referință (50-100 kg). Calificativul nu poate fi determinat.
                        </div>
                      )
                    )}
                  </>
                )}
              </CardContent>
            )}
          </Card>

          <div className="space-y-2">
            <Label htmlFor="observatii-2">Observații Suplimentare</Label>
            <Textarea
              id="observatii-2"
              data-testid="atelier-2-observatii"
              rows={3}
              placeholder="Adăugați observații suplimentare..."
              {...register("observatii")}
            />
          </div>

          {/* PIPA — Program Individualizat de Pregătire și Antrenament.
              Bifarea se propagă în secțiunea "Antrenament" din Concluzii aviz medico-sportiv. */}
          <div className="rounded-md border p-3 bg-slate-50 flex items-start gap-2" data-testid="atelier-2-pipa-row">
            <Checkbox
              id="atelier-2-pipa"
              checked={!!watchedValues.pipa}
              onCheckedChange={(v) => setValue("pipa", !!v)}
              data-testid="atelier-2-pipa-check"
            />
            <div className="flex-1">
              <Label htmlFor="atelier-2-pipa" className="text-sm font-medium cursor-pointer">
                PIPA — Program Individualizat de Pregătire și Antrenament
              </Label>
              <p className="text-xs text-slate-600 mt-0.5">
                Bifat → apare bolduit în secțiunea „Antrenament&rdquo; din Concluzii aviz medico-sportiv.
              </p>
            </div>
          </div>

          {/* Monitorizare puls — se propagă în secțiunea "Antrenament" din Concluzii aviz medico-sportiv. */}
          <div className="rounded-md border p-3 bg-slate-50 flex items-start gap-2" data-testid="atelier-2-monitorizare-puls-row">
            <Checkbox
              id="atelier-2-monitorizare-puls"
              checked={!!watchedValues.monitorizare_puls}
              onCheckedChange={(v) => setValue("monitorizare_puls", !!v)}
              data-testid="atelier-2-monitorizare-puls-check"
            />
            <div className="flex-1">
              <Label htmlFor="atelier-2-monitorizare-puls" className="text-sm font-medium cursor-pointer">
                Monitorizare puls
              </Label>
              <p className="text-xs text-slate-600 mt-0.5">
                Bifat → apare bolduit în secțiunea „Antrenament&rdquo; din Concluzii aviz medico-sportiv.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 pt-4 border-t">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="completat-2"
                data-testid="atelier-2-completat"
                checked={completat}
                onCheckedChange={(checked) => setValue("completat", checked)}
              />
              <Label htmlFor="completat-2" className="text-sm font-medium cursor-pointer">
                Marchează ca și completat
              </Label>
            </div>
            <div className="flex-1" />
            <GenerateVizaButton patient={patientData} onGenerated={onUpdate} />
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Anulează
            </Button>
            <Button type="submit" disabled={isLoading} data-testid="atelier-2-save">
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvează
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
